import React, { useState, useEffect } from 'react';
import { FaChartPie, FaUsers, FaGlobe, FaEnvelope, FaUserShield, FaBullhorn, FaCog, FaVideo } from 'react-icons/fa';
import { AdminAPI } from '../utils/adminApi';
import { getToken } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AdminDashboard from '../components/Admin/AdminDashboard';
import UserManagement from '../components/Admin/UserManagement';
import PublicContentManagement from '../components/Admin/PublicContentManagement';
import WhitelistManagement from '../components/Admin/WhitelistManagement';
import UserDetailModal from '../components/Admin/UserDetailModal';
import AnnouncementManagement from '../components/AnnouncementManagement';
import StreamManagement from '../components/Admin/StreamManagement';
import ConfirmDialog from '../components/Admin/ConfirmDialog';

const AdminPage: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'public' | 'whitelist' | 'announcements' | 'stream' | 'news-permissions' | 'settings'>('dashboard');
    const [stats, setStats] = useState<any>(null);
    const [users, setUsers] = useState<any[]>([]);
    const [publicContent, setPublicContent] = useState<any>(null);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [userContent, setUserContent] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [searchTerm, setSearchTerm] = useState('');

    // Settings State
    const [maintenanceMode, setMaintenanceMode] = useState(false);

    // New state for sorting
    const [sortField, setSortField] = useState('created_at');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

    // Confirm Dialog State for Reset Password
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [userToReset, setUserToReset] = useState<any>(null);

    useEffect(() => {
        loadData();
    }, [activeTab, pagination.page, sortField, sortOrder]);

    const loadData = async () => {
        const token = getToken();
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            // Guard handled authentication
            // const payload = JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
            console.error('Failed to parse JWT:', e);
        }

        try {
            setLoading(true);

            if (activeTab === 'dashboard') {
                const statsData = await AdminAPI.getStats(token);
                setStats(statsData);
            } else if (activeTab === 'users') {
                const usersData = await AdminAPI.getUsers({
                    page: pagination.page,
                    limit: pagination.limit,
                    search: searchTerm,
                    sort: sortField,
                    order: sortOrder
                }, token);
                setUsers(usersData.users);
                setPagination(usersData.pagination);
            } else if (activeTab === 'public') {
                const data = await AdminAPI.getPublicContent(token);
                setPublicContent(data);
            } else if (activeTab === 'settings') {
                const { SystemAPI } = await import('../utils/api');
                const status = await SystemAPI.getStatus();
                setMaintenanceMode(status.maintenance_mode);
            }
        } catch (error: any) {
            if (error.message.includes('403') || error.message.includes('Forbidden')) {
                toast.error('Truy cập bị từ chối: Cần quyền admin');
                navigate('/');
            } else {
                console.error('Error loading admin data:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = (user: any) => {
        setUserToReset(user);
        setIsConfirmOpen(true);
    };

    const confirmResetPassword = async () => {
        if (!userToReset) return;

        const token = getToken();
        if (!token) return;

        try {
            await AdminAPI.resetUserPassword(userToReset.id, token);
            toast.success(`Đã reset mật khẩu thành công! Mật khẩu mới: 123456789`);
            setIsConfirmOpen(false);
            setUserToReset(null);
        } catch (error) {
            console.error('Error resetting password:', error);
            toast.error('Không thể reset mật khẩu');
        }
    };

    const handleViewUser = async (user: any) => {
        const token = getToken();
        if (!token) return;

        try {
            const content = await AdminAPI.getUserContent(user.id, token);
            setUserContent(content);
            setSelectedUser(user);
        } catch (error) {
            console.error('Error loading user content:', error);
            toast.error('Không thể tải thông tin user');
        }
    };

    const handleTogglePublic = async (type: 'class' | 'quiz', id: string) => {
        const token = getToken();
        if (!token) return;

        try {
            if (type === 'class') {
                await AdminAPI.toggleClassPublic(id, token);
            } else {
                await AdminAPI.toggleQuizPublished(id, token);
            }
            toast.success('Đã cập nhật trạng thái!');
            loadData();
        } catch (error) {
            console.error('Error toggling visibility:', error);
            toast.error('Không thể cập nhật');
        }
    };

    if (loading && !stats && users.length === 0 && !publicContent) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-xl text-gray-700 dark:text-gray-300 animate-pulse">Đang tải...</div>
            </div>
        );
    }

    return (
        <>
            {/* Hero Section - Full Width */}
            <div className="relative overflow-hidden group bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 dark:from-blue-900 dark:via-slate-900 dark:to-slate-950 shadow-2xl animate-slideDownIn">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl mix-blend-overlay"></div>

                {/* Overlay pattern */}
                <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)] bg-[size:24px_24px] pointer-events-none"></div>

                {/* Shimmer Effect */}
                <div
                    className="
                        absolute inset-0
                        opacity-20
                        bg-gradient-to-r from-transparent via-white/40 to-transparent
                        blur-[2px]
                        animate-[shimmer_3s_ease-in-out_infinite]
                        [mask-image:linear-gradient(to_right,transparent_0%,black_20%,black_80%,transparent_100%)]
                        mix-blend-overlay
                        pointer-events-none
                    "
                ></div>

                <div className="max-w-screen-2xl mx-auto p-8 sm:p-12">
                    <div className="relative z-10">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-white mb-4 tracking-tight">
                            Quản trị hệ thống
                        </h1>
                        <p className="text-base font-mono sm:text-lg text-blue-100 dark:text-blue-200 max-w-2xl leading-relaxed">
                            Quản lý người dùng, nội dung và cài đặt hệ thống
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 sm:gap-4 mb-6 border-b-2 border-gray-200 dark:border-gray-700">
                    {[
                        { key: 'dashboard', icon: FaChartPie, label: 'Tổng Quan' },
                        { key: 'users', icon: FaUsers, label: 'Người Dùng' },
                        { key: 'public', icon: FaGlobe, label: 'Nội Dung Public' },
                        { key: 'whitelist', icon: FaEnvelope, label: 'Whitelist' },

                        { key: 'stream', icon: FaVideo, label: 'Live Stream' },

                        { key: 'settings', icon: FaCog, label: 'Hệ Thống' }
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => {
                                setActiveTab(tab.key as any);
                            }}
                            className={`px-4 sm:px-6 py-2 sm:py-3 font-semibold transition-all duration-300 rounded-t-lg ${activeTab === tab.key
                                ? 'border-b-4 border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 -mb-0.5'
                                : 'text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                        >
                            <span className="hidden sm:inline-flex items-center gap-2"><tab.icon /> {tab.label}</span>
                            <span className="sm:hidden text-xl"><tab.icon /></span>
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="min-h-[400px]">
                    {activeTab === 'dashboard' && <AdminDashboard stats={stats} />}

                    {activeTab === 'users' && (
                        <UserManagement
                            users={users}
                            pagination={pagination}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            setPagination={setPagination}
                            loadData={loadData}
                            handleViewUser={handleViewUser}
                            // New props

                            sortField={sortField}
                            setSortField={setSortField}
                            sortOrder={sortOrder}
                            setSortOrder={setSortOrder}
                            handleResetPassword={handleResetPassword}
                            handleUpdateRole={async (userId, newRole) => {
                                const token = getToken();
                                if (!token) return;
                                try {
                                    await AdminAPI.updateUserRole(userId, newRole, token);
                                    toast.success('Cập nhật vai trò thành công!');
                                    loadData();
                                } catch (error: any) {
                                    console.error('Failed to update role:', error);
                                    toast.error(error.message || 'Không thể cập nhật vai trò');
                                }
                            }}
                        />
                    )}

                    {activeTab === 'public' && (
                        <PublicContentManagement
                            publicContent={publicContent}
                            handleTogglePublic={handleTogglePublic}
                        />
                    )}

                    {activeTab === 'whitelist' && (
                        <WhitelistManagement loadData={loadData} />
                    )}

                    {activeTab === 'announcements' && (
                        <AnnouncementManagement />
                    )}

                    {activeTab === 'stream' && (
                        <StreamManagement />
                    )}


                    {activeTab === 'settings' && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 animate-fadeIn">
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">
                                <FaCog className="text-blue-500" />
                                Cài đặt hệ thống
                            </h2>

                            <div className="space-y-6">
                                {/* Maintenance Mode Toggle */}
                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div>
                                        <h3 className="font-semibold text-gray-800 dark:text-gray-200">Chế độ bảo trì</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                            Khi bật, chỉ Admin mới có thể truy cập website. Người dùng thường sẽ thấy trang thông báo bảo trì.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={maintenanceMode}
                                            onChange={async (e) => {
                                                const newValue = e.target.checked;
                                                const token = getToken();
                                                if (!token) return;

                                                try {
                                                    await import('../utils/api').then(({ SystemAPI }) =>
                                                        SystemAPI.toggleMaintenance(newValue, token)
                                                    );
                                                    setMaintenanceMode(newValue);
                                                    toast.success(`Đã ${newValue ? 'BẬT' : 'TẮT'} chế độ bảo trì`);
                                                } catch (error) {
                                                    toast.error('Lỗi khi cập nhật trạng thái');
                                                    console.error(error);
                                                }
                                            }}
                                        />
                                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-red-500"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}


                </div>

                {/* Modals */}
                {selectedUser && (
                    <UserDetailModal
                        selectedUser={selectedUser}
                        userContent={userContent}
                        setSelectedUser={setSelectedUser}
                        setUserContent={setUserContent}
                        loadData={loadData}
                        handleUpdateRole={async (userId, newRole) => {
                            const token = getToken();
                            if (!token) return;
                            try {
                                await AdminAPI.updateUserRole(userId, newRole as 'admin' | 'user', token);
                                toast.success('Cập nhật vai trò thành công!');
                                // Update local selected user state to reflect change immediately
                                setSelectedUser({ ...selectedUser, role: newRole });
                                loadData();
                            } catch (error: any) {
                                console.error('Failed to update role:', error);
                                toast.error(error.message || 'Không thể cập nhật vai trò');
                            }
                        }}
                    />
                )}



                {/* Confirm Dialog */}
                <ConfirmDialog
                    isOpen={isConfirmOpen}
                    title="Xác nhận Reset Mật khẩu"
                    message={`Bạn có chắc chắn muốn reset mật khẩu của người dùng ${userToReset?.email} về mặc định (123456789) không?`}
                    confirmLabel="Xác nhận"
                    onConfirm={confirmResetPassword}
                    onCancel={() => setIsConfirmOpen(false)}
                    isDestructive={true}
                />
            </div>
        </>
    );
};

export default AdminPage;
